import { Injectable, Optional } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { AssetClass, BlotterType } from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
// import { PercentAwayHelper } from './orders/PercentAwayHelper';
// import { OrderTypeHelper } from './orders/OrderTypeHelper';
// import { OrderStateHelper } from './orders/OrderStateHelper';
// import { ValueDateHelper } from './orders/ValueDateHelper';
// import { NdfTrackingFwdPtsHelper } from './orders/NdfTrackingFwdPtsHelper';
import {
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
  CellClassParams,
} from "ag-grid-community";
import { FixingTimeAdapter } from "../adapter/benchmark/FixingTimeAdapter";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";
import { DealtPricingService } from "../lookup/pricing/impl/DealtPricingService";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { BaseTableDefinitionFactory, EMPTY_AMT_RATE_VALUE } from "./BaseTableDefinitionFactory";
import { ColumnFilterType } from "./ColumnFilterType";
import { ProfileSettingKeys } from "./core/ProfileSettingKeys";
import { SysFeatures } from "./core/SysFeatures";
import { FilterOrderTypes, OrderBlotterFilterOrderStates } from "./FilterTypeEnums";
import { IndexType } from "./IndexType";
import { ActivationHelper } from "./orders/ActivationHelper";
import { ExpiryHelper } from "./orders/ExpiryHelper";
// import { StatusIconsHelper } from './orders/StatusIconsHelper';
import {
  COLUMN_DEALT_DIR,
  COLUMN_INSTRUMENT,
  COLUMN_ORDER_AMOUNT,
  COLUMN_ORDER_STATE,
  COLUMN_ORDER_TYPE,
  COLUMN_TRADER_GROUP_NAMES,
  FIXING_DATE_COLUMN_PROPERTY,
  REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
  REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
  REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
  TIME_ZONE_UTC,
} from "./OrdersTableDefinitionFactory.constants";
// Adapters
import { OrdersTableMode } from "./OrdersTableMode";
import { BfxColDef, TableDefinition } from "./TableDefinition";
import { HOMS_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import {
  BMARK_DEFAULT_NAME,
  COLUMN_ACCOUNT_ID,
  COLUMN_ALGO_PARAMETERS,
  COLUMN_CCY_PAIR_CODE,
  COLUMN_END_TIME,
  COLUMN_INTER_DESK_ID,
  COLUMN_PRIVATE_COMMENT,
  COLUMN_START_TIME,
  COLUMN_TRADE_DESK_ID,
  COLUMN_TRADE_ORDER_TYPE_DATA,
  COMMENT_EDITOR,
  EDITOR_AMOUNT,
  EDITOR_AUTO_COMPLETE,
  EDITOR_TIME,
  ORDER_STATE_ERROR,
  ORDER_STATE_REJECTED,
  ORDER_STATE_STAGING,
  ORDER_STATE_SUBMITTED,
} from "./HedgeOrderMonitorStagingTableDefinitionFactory.constants";
import { BfxHedgeOrderStagingRow } from "./benchmarks/BfxHedgeOrderStagingRow";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";

const LOCKABLE_COLUMNS: string[] = [COLUMN_CCY_PAIR_CODE, COLUMN_DEALT_DIR, COLUMN_ORDER_AMOUNT];

const EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES: any = {
  blotter_primary_col: (params: CellClassParams) => params.context.isCellEditingEnabled(params),
  bfx_cell_editable: (params: CellClassParams) => params.context.isCellEditingEnabled(params),
  hedge_order_staging_state_processing: (params: CellClassParams) => params.data.processing,
  hedge_order_hidden_cell: (params: CellClassParams) =>
    (params.colDef.field == COLUMN_ORDER_TYPE ||
      params.colDef.field == COLUMN_START_TIME ||
      params.colDef.field == COLUMN_END_TIME) &&
    params.value == null &&
    !params.context.isCellEditingEnabled(params),
};

@Injectable()
export class HedgeOrderMonitorStagingTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "creationDate", sort: "desc" }];
  protected mode: OrdersTableMode = OrdersTableMode.DEALT;
  private myPrivateCommentsEnabled: boolean;
  protected myNdfEnabled: boolean;
  protected myFwdEnabled: boolean;
  protected myTrailingStopEnabled: boolean;
  protected myAutFillEnabled: boolean;
  protected myBmarkEnabled: boolean;
  protected myPartFillDisabledTypeText: string = "";
  myBmarkTradeGroupNameHelper: any;
  protected myActivationHelper: ActivationHelper;
  protected myExpiryHelper: ExpiryHelper;
  public bmarkShortNm: string;

  constructor(
    profileService: ProfilePromiseActionService,
    // It is not possible to inject against an interface
    @Optional() private _ccyPairLookupService: CcyPairLookupService,
    @Optional() private _sysConfigService: SystemConfigPromiseService,
    @Optional() protected _translateService: TranslateService,
    @Optional()
    protected algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional() protected _myClientLookup: ClientLookupService,
    @Optional() protected _rateSourceLookup: RateSourceLookupService,
    @Optional() protected _myAccountLookup: AccountLookupService,
    @Optional() protected _mySalesGroupLookup: SalesGroupLookupService,
    @Optional() protected _myTraderGroupLookup: TraderGroupLookupService,
    @Optional() protected _userLookupService: UserLookupService,
    @Optional() protected _myBasePricingService: BasePricingService,
    @Optional() protected _myDealtPricingService: DealtPricingService,
    @Optional()
    protected _fixingSourceLookupService: FixingSourceLookupService,
    @Optional()
    protected _currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    protected atdlTooltipUiRep: AtdlTooltipUiRep,
  ) {
    super(profileService);

    // this.dealtPercentAwayHelper = new PercentAwayHelper(
    //     this._myDealtPricingService,
    //     profileService,
    //     this._ccyPairLookupService
    // );
    // this.basePercentAwayHelper = new PercentAwayHelper(
    //     this._myBasePricingService,
    //     profileService,
    //     this._ccyPairLookupService
    // );
    // this.myOrderTypeHelper = new OrderTypeHelper(
    //     this.myPartFillDisabledTypeText,
    //     this.algoStrategyLookupService
    // );
    // this.myOrderStateHelper = new OrderStateHelper(
    //     COLUMN_ORDER_STATE,
    //     this._translateService
    // );
    // this.myStatusIconHelper = new StatusIconsHelper(
    //     this._userLookupService,
    //     this._sysConfigService,
    //     this._translateService,
    //     this._myDealtPricingService,
    //     this._myTraderGroupLookup
    // );
    // this.myValueDateHelper = new ValueDateHelper(
    //     this._currencyPairCalendarLookupService
    // );
    // this.myNdfTrackingFwdPointsHelper = new NdfTrackingFwdPtsHelper(
    //     this._ccyPairLookupService,
    //     this._myBasePricingService,
    //     this._myDealtPricingService
    // );
    // this.myBmarkTradeGroupNameHelper = new BmarkTraderGroupNameHelper(
    //     this._traderGroupLookup,
    //     COLUMN_DESK_ID
    // );
    this.myActivationHelper = new ActivationHelper();
    this.myExpiryHelper = new ExpiryHelper();
    this.profileService.getProfileConfig(this.getProfileSettingKey()).then((profileValue) => {
      this.mapUserSettings(profileValue);
    });

    this.profileService
      .getProfileConfigUpdates(this.getProfileSettingKey())
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
      });

    this.getBmarkOrderDisplayName();
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this._sysConfigService.getSystemConfig(SysFeatures.clientTraderUsersEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.featuresEnablePrivateComments),
      this._sysConfigService.getSystemConfig(SysFeatures.markupEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.ndfEnabled),
      this._sysConfigService.getSystemConfig(SysFeatures.fwdEnabled),
      // non config promise
      this.atdlTooltipUiRep.isReady(),
    ];
  }

  protected determineDefaultConfig(defaultConfig: any) {
    super.determineDefaultConfig(defaultConfig);
    if (defaultConfig) {
      if (defaultConfig.dealtMode != null) {
        this.mode = defaultConfig.dealtMode ? OrdersTableMode.DEALT : OrdersTableMode.BASE;
      }
      if (defaultConfig.ndfMode != null && defaultConfig.ndfMode) {
        this.mode =
          this.mode === OrdersTableMode.BASE ? OrdersTableMode.NDF_BASE : OrdersTableMode.NDF_DEALT;
      }
    }
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myPrivateCommentsEnabled = this.valueOn(values[1]);
    this.myNdfEnabled = this.valueOn(values[3]);
    this.myFwdEnabled = this.valueOn(values[4]);
  }

  private initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      hide: false,
    };
  }

  public createDef(): TableDefinition {
    super.createDef();
    logger.out.info(`${this.constructor.name}.createDef() ${OrdersTableMode[this.mode]}`);
    let fwdColsVisibleByDefault: boolean = this.myNdfEnabled || this.myFwdEnabled;

    let position = this.position;
    let td = new TableDefinition("id", "id");

    // requestSubmissionTime's column
    td.setDefaultSort(1, "desc");

    /**
     * Selection column
     */
    let selectionCol: BfxColDef = this.initColdDef();
    selectionCol.field = "selection";
    selectionCol.sortingOrder = [];
    selectionCol.pinned = "left";
    selectionCol.lockPosition = true;
    selectionCol.checkboxSelection = function (params: any) {
      return !(params != null && params.data != null && params.data.bulkEditRow);
    };
    selectionCol.headerName = "";
    selectionCol.headerCheckboxSelection = true;
    selectionCol.headerCheckboxSelectionFilteredOnly = true;
    selectionCol.sortable = false;
    selectionCol.suppressMovable = true;
    selectionCol.resizable = false;
    selectionCol.suppressMenu = true;
    selectionCol.columnFilterType = ColumnFilterType.NONE;
    selectionCol.cellClass = function (params: any) {
      if (params != null && params.data != null && params.data.bulkRowEdit) {
        return [""];
      }
      return ["status-icon-cell"];
    }.bind(this);
    selectionCol.width = 37;
    selectionCol.minWidth = 37;
    selectionCol.maxWidth = 37;
    selectionCol.omitFromPicker = true;
    selectionCol.indexType = IndexType.None;
    selectionCol.position = this.position.next();
    td.addColumn(selectionCol);

    // 1: Status
    let orderStateCol: BfxColDef = this.initColdDef();
    orderStateCol.headerName = "Status";
    orderStateCol.indexType = IndexType.String;
    orderStateCol.field = COLUMN_ORDER_STATE;
    orderStateCol.minWidth = this.fiveWs;
    orderStateCol.width = this.sevenWs;
    orderStateCol.columnFilterType = ColumnFilterType.ORDERSTATE;
    orderStateCol.columnFilterData = OrderBlotterFilterOrderStates;
    orderStateCol.disableInPicker = true;
    orderStateCol.cellClass = ["blotter_left_align_col"];
    orderStateCol.tooltipValueGetter = (params: ITooltipParams) => {
      if (params != null && params.data != null && params.data.bulkEditRow) {
        return "";
      }
      let value: string = params.value;

      if (params.data.errors != null) {
        if (params.data.errors.length > 0) {
          value = params.data.errors
            .map((error: string) => this._translateService.instant(error))
            .join(",\n");
        } else {
          value = params.data.errors;
        }
      }

      return value;
    };
    orderStateCol.valueGetter = (params: ValueGetterParams) => {
      let value: string = params.data.orderState;

      if (params.data.bulkEditRow) {
        return "Bulk Edit";
      } else if (value == null) {
        value =
          params.data.errors != null && params.data.errors.length > 0
            ? params.data.submitted
              ? ORDER_STATE_REJECTED
              : ORDER_STATE_ERROR
            : params.data.processing
            ? ORDER_STATE_SUBMITTED
            : ORDER_STATE_STAGING;
      }

      return value;
    };
    orderStateCol.cellClassRules = {
      hedge_order_staging_state_rejected: (params: CellClassParams) =>
        params.data.errors != null && params.data.errors.length > 0,
    };

    if (!this.restoreFromConfig(orderStateCol)) {
      orderStateCol.hide = false;
      orderStateCol.position = this.position.next();
    }

    td.addColumn(orderStateCol);

    // 2: Locked
    let lockCol: BfxColDef = this.initColdDef();
    lockCol.headerName = "";
    lockCol.indexType = IndexType.String;
    lockCol.field = "locked";
    lockCol.minWidth = 34;
    lockCol.maxWidth = 34;
    lockCol.width = 34;
    lockCol.resizable = false;
    lockCol.hide = false;
    lockCol.disableInPicker = true;
    lockCol.omitFromPicker = true;
    lockCol.cellRenderer = "lockedRenderer";
    lockCol.position = this.position.next();
    lockCol.cellClass = ["blotter_center_align_col status-icon-cell"];
    lockCol.tooltipValueGetter = (params: ITooltipParams) => {
      return params.data.locked ? "Locked" : "Unlocked";
    };

    td.addColumn(lockCol);

    // 3: Pair
    let pairCol: BfxColDef = this.initColdDef();
    pairCol.headerName = "Pair";
    pairCol.field = COLUMN_CCY_PAIR_CODE;
    pairCol.indexType = IndexType.String;
    pairCol.columnFilterType = ColumnFilterType.CURRENCY_PAIR;
    pairCol.editable = this.isEditable.bind(this);
    pairCol.cellEditor = EDITOR_AUTO_COMPLETE;
    pairCol.cellEditorParams = { size: "sm" };
    pairCol.cellClass = ["left_col", "autocomplete-col"];
    pairCol.disableInPicker = true;
    pairCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    pairCol.valueGetter = function (params) {
      if (params != null && params.data != null && params.data.bulkEditRow) {
        return "";
      } else if (params.data && params.data.orderRequest) {
        let orderRequest: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
        if (orderRequest.base != null && orderRequest.nonBase != null) {
          return orderRequest.base + orderRequest.nonBase;
        }
      }
      return "-";
    };
    pairCol.alias = [COLUMN_INSTRUMENT];
    pairCol.minWidth = this.fiveWs;
    pairCol.width = this.sixWs;
    pairCol.tooltipValueGetter = (params: ITooltipParams) => {
      return params.value;
    };
    if (!this.restoreFromConfig(pairCol)) {
      pairCol.hide = false;
      pairCol.position = this.position.next();
    }
    td.addColumn(pairCol);

    // 4: Dir
    let intentCol: BfxColDef = this.initColdDef();
    intentCol.headerName = "Dir";
    intentCol.field = COLUMN_DEALT_DIR;
    intentCol.indexType = IndexType.String;
    intentCol.minWidth = this.threeWs;
    intentCol.width = this.fourWs;
    intentCol.editable = this.isEditable.bind(this);
    intentCol.cellRenderer = "intentRenderer";
    intentCol.cellEditor = "intentEditor";
    intentCol.cellRendererParams = { renderer: true };
    intentCol.cellEditorParams = { renderer: false };
    intentCol.cellClass = ["blotter_center_align_col"];
    intentCol.columnFilterType = ColumnFilterType.NONE;
    intentCol.columnFilterData = CubiqTS.OrderIntent;
    intentCol.disableInPicker = true;
    intentCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    intentCol.valueGetter = function (params) {
      if (params != null && params.data != null && params.data.bulkEditRow) {
        return "omitRow";
      }

      let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
      let clientIntent: CubiqTS.OrderIntent = order.clientIntent;

      if (clientIntent == null) {
        return null;
      }

      switch (clientIntent) {
        case CubiqTS.OrderIntent.BUY:
          return "Buy";
        case CubiqTS.OrderIntent.SELL:
          return "Sell";
      }
    };
    intentCol.valueFormatter = (params: ValueFormatterParams) => {
      return params.value ? params.value : "-";
    };

    if (!this.restoreFromConfig(intentCol)) {
      intentCol.hide = false;
      intentCol.position = this.position.next();
    }

    td.addColumn(intentCol);

    // 5: Order Amt
    let orderAmtCol: BfxColDef = this.initColdDef();
    orderAmtCol.headerName = "Amount";
    orderAmtCol.field = COLUMN_ORDER_AMOUNT;
    orderAmtCol.indexType = IndexType.NaturalString;
    orderAmtCol.showTooltip = true;
    orderAmtCol.minWidth = 90;
    orderAmtCol.width = this.eightWs;
    orderAmtCol.editable = this.isEditable.bind(this);
    orderAmtCol.cellEditor = EDITOR_AMOUNT;
    orderAmtCol.cellClass = ["blotter_amt_align"];
    orderAmtCol.valueFormatter = this.formatAmts;
    orderAmtCol.columnFilterType = ColumnFilterType.INT;
    orderAmtCol.columnFilterData = null;
    orderAmtCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    orderAmtCol.disableInPicker = true;
    orderAmtCol.tooltipValueGetter = (params: ITooltipParams) => {
      return params.valueFormatted;
    };

    if (!this.restoreFromConfig(orderAmtCol)) {
      orderAmtCol.hide = false;
      orderAmtCol.position = this.position.next();
    }

    td.addColumn(orderAmtCol);

    // 6: Trader Group
    let traderGroupCol: BfxColDef = this.initColdDef();
    traderGroupCol.headerName = "Trader Group";
    traderGroupCol.field = COLUMN_TRADER_GROUP_NAMES; //COLUMN_DESK_ID;
    traderGroupCol.alias = [COLUMN_TRADE_DESK_ID, COLUMN_INTER_DESK_ID];
    traderGroupCol.minWidth = this.eightWs;
    traderGroupCol.width = this.eightWs;
    traderGroupCol.editable = this.isEditable.bind(this);
    traderGroupCol.columnFilterType = ColumnFilterType.TRADERGROUP;
    traderGroupCol.disableInPicker = true;
    traderGroupCol.cellEditor = EDITOR_AUTO_COMPLETE;
    traderGroupCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    traderGroupCol.valueGetter = ((params: ValueGetterParams) => {
      let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
      return order.traderGroup;
    }).bind(this);
    traderGroupCol.valueFormatter = (params: ValueFormatterParams) => {
      return params.value ? params.value : "-";
    };
    traderGroupCol.tooltipValueGetter = (params: ITooltipParams) => {
      return params.value;
    };
    if (!this.restoreFromConfig(traderGroupCol)) {
      traderGroupCol.hide = false;
      traderGroupCol.position = this.position.next();
    }

    td.addColumn(traderGroupCol);

    // 7: Order Type
    let orderTypeCol: BfxColDef = this.initColdDef();
    orderTypeCol.headerName = "Type";
    orderTypeCol.field = COLUMN_ORDER_TYPE;
    orderTypeCol.indexType = IndexType.String;
    orderTypeCol.alias = [COLUMN_TRADE_ORDER_TYPE_DATA];
    orderTypeCol.editable = this.isEditable.bind(this);
    orderTypeCol.minWidth = 90;
    orderTypeCol.width = this.sixWs;
    orderTypeCol.cellClass = ["blotter_left_align"];
    orderTypeCol.cellEditor = EDITOR_AUTO_COMPLETE;
    orderTypeCol.columnFilterType = ColumnFilterType.ORDERTYPE;
    orderTypeCol.columnFilterData = FilterOrderTypes;
    orderTypeCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    orderTypeCol.disableInPicker = true;
    orderTypeCol.valueGetter = function (params) {
      let row: BfxHedgeOrderStagingRow = params.data;
      let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;

      if (order.orderType == CubiqTS.OrderType.BMALGO) {
        return row.algoHedgeConfig != null ? row.algoHedgeConfig.algoProfile : "-";
      } else if (order.orderType == CubiqTS.OrderType.BENCHMARK) {
        return "BMHO";
      }

      return order.orderType;
    }.bind(this);
    orderTypeCol.valueFormatter = (param: ValueFormatterParams) => {
      return param.value == null ? "-" : param.value;
    };
    orderTypeCol.tooltipValueGetter = (params: ITooltipParams) => {
      return params.value;
    };
    if (!this.restoreFromConfig(orderTypeCol)) {
      orderTypeCol.hide = false;
      orderTypeCol.position = this.position.next();
    }

    td.addColumn(orderTypeCol);

    // 8: Start Time
    let startTimeColumn: BfxColDef = this.initColdDef();
    startTimeColumn.headerName = "Start Time";
    startTimeColumn.field = COLUMN_START_TIME;
    startTimeColumn.minWidth = this.sixWs;
    startTimeColumn.width = this.sixWs;
    startTimeColumn.indexType = IndexType.String;
    startTimeColumn.columnFilterType = ColumnFilterType.NONE;
    startTimeColumn.visibleInEmbeddedMode = true;
    startTimeColumn.editable = this.isEditable.bind(this);
    startTimeColumn.cellEditor = EDITOR_TIME;
    startTimeColumn.isPriority = true;
    startTimeColumn.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    startTimeColumn.valueFormatter = this.formatDate.bind(this, "HH:mm:ss");
    if (!this.restoreFromConfig(startTimeColumn)) {
      startTimeColumn.hide = false;
      startTimeColumn.position = position.next();
    }
    startTimeColumn.tooltipValueGetter = (params: ITooltipParams) => {
      return params.valueFormatted;
    };

    td.addColumn(startTimeColumn);

    // 9: End Time
    let endTimeColumn: BfxColDef = this.initColdDef();
    endTimeColumn.headerName = "End Time";
    endTimeColumn.field = COLUMN_END_TIME;
    endTimeColumn.minWidth = this.sixWs;
    endTimeColumn.width = this.sixWs;
    endTimeColumn.indexType = IndexType.Double;
    endTimeColumn.columnFilterType = ColumnFilterType.NONE;
    endTimeColumn.cellEditor = EDITOR_TIME;
    endTimeColumn.editable = this.isEditable.bind(this);
    endTimeColumn.visibleInEmbeddedMode = true;
    endTimeColumn.isPriority = true;
    endTimeColumn.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    endTimeColumn.valueFormatter = this.formatDate.bind(this, "HH:mm:ss");
    if (!this.restoreFromConfig(endTimeColumn)) {
      endTimeColumn.hide = false;
      endTimeColumn.position = position.next();
    }
    endTimeColumn.tooltipValueGetter = (params: ITooltipParams) => {
      return params.valueFormatted;
    };

    td.addColumn(endTimeColumn);

    // 10: Account ID
    let accountIdCol: BfxColDef = this.initColdDef();
    accountIdCol.headerName = "Account ID";
    accountIdCol.field = COLUMN_ACCOUNT_ID;
    accountIdCol.indexType = IndexType.String;
    accountIdCol.editable = this.isEditable.bind(this);
    accountIdCol.minWidth = this.sevenWs;
    accountIdCol.cellEditor = EDITOR_AUTO_COMPLETE;
    accountIdCol.width = this.tenWs;
    accountIdCol.columnFilterType = ColumnFilterType.ACCOUNTID;
    accountIdCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    accountIdCol.tooltipValueGetter = (params: ITooltipParams) => {
      return params.value;
    };
    if (!this.restoreFromConfig(accountIdCol)) {
      accountIdCol.hide = false;
      accountIdCol.position = this.position.next();
    }
    accountIdCol.valueGetter = function (params) {
      let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
      return order.accountCode == null ? "-" : order.accountCode;
    };

    td.addColumn(accountIdCol);

    // 11: Private Comments
    if (this.myPrivateCommentsEnabled) {
      let privateCommCol: BfxColDef = this.initColdDef();
      privateCommCol.headerName = "Private Comments";
      privateCommCol.field = COLUMN_PRIVATE_COMMENT;
      privateCommCol.indexType = IndexType.String;
      privateCommCol.minWidth = this.sevenWs;
      privateCommCol.width = this.tenWs;
      privateCommCol.editable = this.isEditable.bind(this);
      privateCommCol.showTooltip = true;
      privateCommCol.columnFilterType = ColumnFilterType.NONE;
      privateCommCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
      /*   privateCommCol.valueSetter = (params: ValueSetterParams) => {
                params.data.orderRequest.comment2 = params.newValue;
                return true;
            }; */
      privateCommCol.cellEditor = COMMENT_EDITOR;

      privateCommCol.tooltipValueGetter = (params: ITooltipParams) => {
        return params.valueFormatted;
      };

      if (!this.restoreFromConfig(privateCommCol)) {
        privateCommCol.hide = false;
        privateCommCol.position = this.position.next();
      }

      privateCommCol.valueGetter = function (params) {
        let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
        return order.comment2 != null && order.comment2.trim().length > 0 ? order.comment2 : null;
      };

      privateCommCol.valueFormatter = (params: ValueFormatterParams) => {
        return params.value == null || params.value.trim().length == 0 ? "-" : params.value;
      };

      td.addColumn(privateCommCol);
    }

    // 12: Algo Parameters
    let algoPramsCol: BfxColDef = this.initColdDef();
    algoPramsCol.headerName = "Strategy Parameters";
    algoPramsCol.colSelectorName = "Strategy Param...";
    algoPramsCol.field = COLUMN_ALGO_PARAMETERS;
    algoPramsCol.indexType = IndexType.String;
    algoPramsCol.minWidth = this.sevenWs;
    algoPramsCol.width = this.tenWs;
    algoPramsCol.disableInPicker = false;
    algoPramsCol.isPrimary = true;
    algoPramsCol.cellClass = ["algo_params_col"];
    algoPramsCol.cellClassRules = EDITABLE_LOCKED_COLUMN_CELL_CLASS_RULES;
    algoPramsCol.tooltipValueGetter = (params: ITooltipParams) => {
      if (params != null && params.data != null && params.data.bulkEditRow) {
        return "";
      }
      let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
      let algoId =
        params.data.algoHedgeConfig != null ? params.data.algoHedgeConfig.orderType : null;
      let value: string = "";
      let strategyParameters = this.atdlTooltipUiRep.cloneWithUiRep(
        algoId,
        order.strategyParameters,
      );

      if (strategyParameters != null) {
        strategyParameters.forEach(
          (param: CubiqTS.StrategyParameterImpl, index: number) =>
            (value += (index > 0 ? "\n" : "") + param.parameterName + " : " + param.parameterValue),
        );
      }

      return value;
    };

    algoPramsCol.cellRenderer = "strategyParamRenderer";
    accountIdCol.columnFilterType = ColumnFilterType.ALGO_PARAMETERS;
    if (!this.restoreFromConfig(algoPramsCol)) {
      algoPramsCol.hide = false;
      algoPramsCol.position = this.position.next();
    }
    algoPramsCol.valueGetter = function (
      this: HedgeOrderMonitorStagingTableDefinitionFactory,
      params,
    ) {
      if (params != null && params.data != null && params.data.bulkEditRow) {
        return "";
      }
      let order: CubiqTS.FixingOrderRequestDataImpl = params.data.orderRequest;
      let algoId =
        params.data.algoHedgeConfig != null ? params.data.algoHedgeConfig.orderType : null;
      let strategyParameters = this.atdlTooltipUiRep.cloneWithUiRep(
        algoId,
        order.strategyParameters,
      );
      let value: string = "";

      if (strategyParameters != null) {
        strategyParameters.forEach(
          (param: CubiqTS.StrategyParameterImpl, index: number) =>
            (value += (index > 0 ? ", " : "") + param.parameterName + " : " + param.parameterValue),
        );
      }

      return value;
    }.bind(this);

    td.addColumn(algoPramsCol);

    return td;
  }

  public getBlotterType() {
    return BlotterType.ORDER;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }

  /**
   * Helper functions
   */

  private mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != null) {
        super.determineDefaultConfig(savedSettings);
        // if (savedSettings.hasOwnProperty('buyAndSellModel')
        // && savedSettings.buyAndSellModel !== null && savedSettings.buyAndSellModel == 'y') {
        //     this.isIntentStyled = true;
        // } else {
        //     this.isIntentStyled = false;
        // }
      }
    } else {
      logger.out.warn(
        "HedgeOrderMonitorStagingTableDefinitionFactory --> mapUserSettings no profile value " +
          profileValue,
      );
    }
  }

  public getBlotterVersion(): string {
    return HOMS_TABLE_DEFINITION_VER;
  }

  protected getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}bmarkHedgeOrderMonitorStagingOrders`;
  }

  protected getFixingTimeValue(
    order: any,
    format: string,
    convertToUTC: boolean,
    fullDateTimeUTC: boolean,
    timeZone: boolean = true,
  ): string {
    let fixingDate: CubiqTS.Moment = order[FIXING_DATE_COLUMN_PROPERTY];
    let reqFixingTimeProperty = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      REQUESTED_FIXING_TIME_COLUMN_PROPERTY,
    );
    let reqFixingDateProperty = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      REQUESTED_FIXING_DATE_COLUMN_PROPERTY,
    );
    let reqTimeZoneCodeProperty: any = FixingTimeAdapter.getFixingDetailsProperty(
      order,
      REQUESTED_FIXING_TIMEZONE_COLUMN_PROPERTY,
    );
    let reqFormattedDateProperty: string = CubiqTS.moment(reqFixingDateProperty).format(format);
    let reqFixingDateFull: string = FixingTimeAdapter.transformDateStringToMomentFormatString(
      reqFixingDateProperty,
      reqFixingTimeProperty,
    );

    if (convertToUTC) {
      if (fullDateTimeUTC) {
        return (
          CubiqTS.moment.utc(fixingDate).format(format) +
          " " +
          TIME_ZONE_UTC +
          " " +
          reqFormattedDateProperty
        );
      }
      return CubiqTS.moment.utc(fixingDate).format(format);
    }

    if (reqFixingDateFull) {
      return (
        CubiqTS.moment.utc(reqFixingDateFull).format(format) +
        (timeZone ? " " + reqTimeZoneCodeProperty : "")
      );
    }

    return "-";
  }

  public formatRate(params: ValueFormatterParams) {
    if (params.value == null) {
      return "-";
    }

    let value: any = params.value;
    if (typeof params.value == "string") {
      value = new CubiqTS.BigNumber(params.value);
    }

    let minPrecision: number = 0;
    let order: CubiqTS.OrderDataImpl = params.data;

    let base: string = order.baseCode;
    let nonBase: string = order.nonBaseCode;

    if (order.allocationData != null && order.allocationData.length > 0) {
      base = order.allocationData[0].baseCcy;
      nonBase = order.allocationData[0].nonbaseCcy;
    }

    let ccyPair: CubiqTS.CurrencyPairImpl = this._ccyPairLookupService.getCcyPair(base, nonBase);

    if (ccyPair) {
      if (order.isInvertedOrderFg) {
        minPrecision = ccyPair.spotInvertDecimalsQty;
      } else {
        minPrecision = ccyPair.pipsQty;
      }
    }

    let currentDecs: number = value.decimalPlaces();
    if (currentDecs < minPrecision) {
      return value.toFixed(minPrecision);
    }

    return value.toFixed(currentDecs);
  }

  async getBmarkOrderDisplayName() {
    this.bmarkShortNm = await this.getBMShortNameFromSysConfig();
  }

  private getBMShortNameFromSysConfig() {
    if (this._sysConfigService) {
      return new Promise<string>((res, rej) => {
        this._sysConfigService
          .getSystemConfig(SysFeatures.fixingOrderShortName)
          .then((value) => {
            let returnValue = value != null && value.length > 0 ? value : BMARK_DEFAULT_NAME;
            res(returnValue);
          })
          .catch((error) => {
            logger.out.error(`OrderTypeAdapter : getSystemConfig :
                        failed to get system config for: ${SysFeatures.fixingOrderShortName}`);
            rej(error.errorMessages);
          });
      });
    }
  }

  protected formatDate(format: string, params: ValueFormatterParams): string {
    if (params.value) return params.value.local().format(format);
    return params.data.isAlgo() ? "-" : "-";
  }

  public isEditable(params: any): boolean {
    let data: BfxHedgeOrderStagingRow = params.node.data;

    if (data.processing) {
      return false;
    }

    // BULK EDIT ROW
    if (data != null && data.bulkEditRow) {
      if (
        params.colDef.field == COLUMN_TRADER_GROUP_NAMES ||
        params.colDef.field == COLUMN_ACCOUNT_ID ||
        params.colDef.field == COLUMN_PRIVATE_COMMENT
      ) {
        return true;
      } else if (params.colDef.field == COLUMN_ORDER_TYPE) {
        return data.orderRequest.orderType != null || data.orderRequest.traderGroup != null;
      } else if (params.colDef.field == COLUMN_START_TIME) {
        return data.isAlgo() || CubiqTS.moment.isMoment(data.startTime);
      } else if (params.colDef.field == COLUMN_END_TIME) {
        return data.isAlgo() || CubiqTS.moment.isMoment(data.endTime);
      }
      return false;
    }

    if (params.colDef.field == COLUMN_START_TIME || params.colDef.field == COLUMN_END_TIME) {
      return data.isAlgo();
    }

    if (params.colDef.field == COLUMN_ALGO_PARAMETERS) {
      return false;
    }

    if (params.colDef.field == COLUMN_ORDER_TYPE) {
      return data.orderRequest.orderType != null;
    }

    if (data != null && LOCKABLE_COLUMNS.indexOf(params.colDef.field) >= 0) {
      return !data.locked;
    }

    return true;
  }

  protected formatAmts(params: ValueFormatterParams): string {
    let data: BfxHedgeOrderStagingRow = params.node.data;
    if (data != null && data.bulkEditRow) {
      return "";
    }

    if (params.value) return params.value.toFormat(0);
    return EMPTY_AMT_RATE_VALUE;
  }
}
